import classes from "./Dashboard.module.css";
import "react-datepicker/dist/react-datepicker.css";
import DatePicker from "react-datepicker";
import PlusSignIcon from "../../images/dashboard/plusSignIcon.svg";
import DeliveryVanIcon from "../../images/dashboard/deliveryVan.svg";
import { format } from "date-fns";
import { BaseTable } from "../../components/UI/baseTable/BaseTable";
import { StatusChip } from "../../components/statusChip/StatusChip";
import { CalendarIcon } from "../../images/dashboard/calendarIcon";
import { fetchDeliveries } from "../../redux/package/thunks/getDeliveryThunk";
import { useTranslations } from "../../helperFunctions/customHooks/useTranslations";
import { formatDateString } from "../../helperFunctions/formatDate";
import { fetchDeliveriesByDate } from "../../redux/package/thunks/getDeliveriesByDateThunk";
import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect, useRef } from "react";
import {
  onEdit,
  setDeliveriesToDisplay,
  setIsEditMode,
  setSelectedDriverInfo,
  setSelectedOrderIndex,
} from "../../redux/globalFunctions/globalFnSlice";
import { setSearchQuery } from "../../redux/misc/slices/searchSlice";
import { getCurrentDate } from "../../helperFunctions/getCurrentDate";

export function Dashboard() {
  const dispatch = useDispatch();
  const datePickerRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isPackageCreated, setIsPackageCreated] = useState(true);
  const today = new Date();
  const { t } = useTranslations();
  const dashboardPage = t.dashboard;

  // Variables for redux state.
  const deliveriesByDate = useSelector(
    (state) => state.deliveriesByDate.deliveries
  );
  const formData = useSelector((state) => state.forms.packageForm);
  const statusByDate = useSelector((state) => state.deliveriesByDate.status);
  const errorByDate = useSelector((state) => state.deliveriesByDate.error);
  const searchQuery = useSelector((state) => state.search.query);
  const deliveriesState = useSelector((state) => state.deliveries);
  const firstname = useSelector((state) => state.user.firstname);
  const createPackageState = useSelector((state) => state.createDelivery);
  const { selectedOrderIndex, deliveriesToDisplay } = useSelector(
    (state) => state.global
  );

  const initialFilters = {
    customer_name: "",
    driver_name: "",
    status: "",
    date: "",
  };

  const [filters, setFilters] = useState({ ...initialFilters });

  useEffect(() => {
    if (statusByDate === "error" && deliveriesState.status === "succeeded") {
      dispatch(setSelectedOrderIndex(0));
      dispatch(setDeliveriesToDisplay(deliveriesState.deliveries));
    } else if (statusByDate === "succeeded") {
      dispatch(setSelectedOrderIndex(0));
      dispatch(setDeliveriesToDisplay(deliveriesByDate));
    }
  }, [statusByDate, deliveriesState.status, selectedDate]);

  // Fetch deliveries by selected date
  useEffect(() => {
    const formattedDate = format(selectedDate, "dd/MM/yyyy");
    dispatch(
      fetchDeliveriesByDate({ driver_name: searchQuery, formattedDate })
    );
  }, [dispatch, selectedDate, searchQuery]);

  // Function for updating the filters from the searchQuery.
  useEffect(() => {
    if (searchQuery !== "") {
      setSelectedDate(getCurrentDate());
      setFilters({
        ...filters,
        driver_name: searchQuery,
      });
    } else {
      // Reset filters when search query is cleared and refetch deliveries.
      setFilters({ ...initialFilters });
    }
  }, [searchQuery]);

  // Fetch last page if deliveriesByDate fetch fails
  useEffect(() => {
    if (statusByDate === "error" && isPackageCreated === true) {
      dispatch(fetchDeliveries({ filters, page: 1 }));
    }
  }, [statusByDate, dispatch, isPackageCreated, filters]);

  // Refetch deliveries when createPackageState.status is "success"
  useEffect(() => {
    if (createPackageState.status === "success") {
      setSelectedDate(today);
      const formattedDate = format(today, "dd/MM/yyyy");
      dispatch(fetchDeliveriesByDate({ formattedDate }));
      setIsPackageCreated(true);
    }
  }, [createPackageState.status]);

  // Function to handle click on a delivery row
  const handleDeliveryClick = (index) => {
    dispatch(setSelectedOrderIndex(index)); // Set the selected delivery index
  };

  const openDatePicker = () => {
    if (datePickerRef.current) {
      datePickerRef.current.setFocus();
    }
  };

  const columns = [
    { label: dashboardPage.table.driver },
    { label: dashboardPage.table.order },
    { label: dashboardPage.table.date },
    { label: dashboardPage.table.address },
    { label: dashboardPage.table.status },
  ];

  function handleAddPackageClick() {
    if (!formData.driver) {
      dispatch(setSelectedDriverInfo(null));
    }
    dispatch(setIsEditMode(false));
    dispatch(onEdit());
  }

  return (
    <section className={classes.dashboard}>
      <div className={`${classes.dashboardTopDiv} fadeInTranslate`}>
        <div className={classes.bgDiv}>
          <div className={classes.contentLeft}>
            <h4>{`${dashboardPage.hello} ${firstname}`}</h4>
            <p>{dashboardPage.welcomeText}</p>
            <button onClick={handleAddPackageClick}>
              <img src={PlusSignIcon} alt={dashboardPage.plusSignIconAlt} />
              {dashboardPage.btnText}
            </button>
          </div>
          <div className={classes.contentRight}>
            <img src={DeliveryVanIcon} alt={dashboardPage.deliveryVanIconAlt} />
          </div>
        </div>
      </div>

      <BaseTable
        status={deliveriesState.status}
        items={deliveriesToDisplay}
        errorMsg={errorByDate}
        noItemsMsg={dashboardPage.noDeliveriesMsg}
        pagination={false}
        columns={columns}
        columnAndRowWidth={"180px"}
        rowStyle={{ cursor: "pointer" }}
        isSelected={selectedOrderIndex}
        onClick={handleDeliveryClick}
        resultsContainerHeight={
          searchQuery === "" ? "calc(100vh - 490px)" : "calc(100vh - 470px)"
        }
        rows={
          deliveriesToDisplay &&
          deliveriesToDisplay?.map((item) => [
            item.driver_name || item.driver?.name || "",
            item.package_id || item.delivery_id,
            item.order_date,
            item.dropoff_address_name,
            <StatusChip status={item.status} hoverText={item.status_notes} />,
          ])
        }
        filters={
          <div className={classes.dashboardFilters}>
            <div className={classes.deliveriesFirstDiv}>
              <div className={classes.firstDivContent}>
                <h6>{dashboardPage.deliveries}</h6>
                <div
                  className={classes.calendarContainer}
                  onClick={() => openDatePicker()}
                  onMouseEnter={() => setHovered(true)}
                  onMouseLeave={() => setHovered(false)}
                >
                  <CalendarIcon hovered={hovered} />
                  <DatePicker
                    selected={selectedDate}
                    onChange={(date) => {
                      setSelectedDate(date);
                      dispatch(setSearchQuery(""));
                    }}
                    dateFormat="dd/MM/yyyy"
                    ref={datePickerRef}
                    onBlur={() => setHovered(false)}
                  />
                </div>
              </div>
            </div>
            {statusByDate === "error" &&
              deliveriesToDisplay.length !== 0 &&
              searchQuery === "" && (
                <div className={classes.lastPageResults}>
                  {dashboardPage.noDeliveriesFound}{" "}
                  {formatDateString(selectedDate)}.{" "}
                  {dashboardPage.showingLastResults}
                </div>
              )}
          </div>
        }
      />
    </section>
  );
}
